import { isNativeApp } from '../utils/platform';

/**
 * RevenueCat SDK wrapper for Apple In-App Purchases.
 * All functions no-op when running on web (non-native).
 *
 * We lazy-import the SDK so web builds never pull in the native Capacitor plugins.
 */

const REVENUECAT_API_KEY = process.env.REACT_APP_REVENUECAT_API_KEY ?? '';
// Accepted RevenueCat key prefixes for iOS:
//   `appl_` — real Apple App Store SDK key (production / TestFlight with real IAPs)
//   `test_` — RevenueCat Test Store key (bypasses StoreKit, uses fake products; great for paywall dev)
const hasValidIosRevenueCatKey =
  REVENUECAT_API_KEY.startsWith('appl_') || REVENUECAT_API_KEY.startsWith('test_');

/** The entitlement identifier configured in RevenueCat dashboard. */
export const ENTITLEMENT_ID = 'Wallstreetle Member';

// ---------------------------------------------------------------------------
// Lazy imports — resolved only on native
// ---------------------------------------------------------------------------

let _Purchases: typeof import('@revenuecat/purchases-capacitor').Purchases | null = null;
let _RevenueCatUI: typeof import('@revenuecat/purchases-capacitor-ui').RevenueCatUI | null = null;
let _LOG_LEVEL: typeof import('@revenuecat/purchases-capacitor').LOG_LEVEL | null = null;
let _PAYWALL_RESULT: typeof import('@revenuecat/purchases-capacitor').PAYWALL_RESULT | null = null;
let _PURCHASES_ERROR_CODE: typeof import('@revenuecat/purchases-capacitor').PURCHASES_ERROR_CODE | null = null;

// NOTE: We split loading (async, returns void) from access (sync, returns plugin).
// Capacitor plugin proxies have a .then() method. If an async function returns
// the plugin directly, `await` treats it as a thenable and calls .then(), which
// throws "UNIMPLEMENTED". By awaiting a Promise<void> and then accessing the
// cached variable synchronously, we avoid this issue.

async function loadPurchases(): Promise<void> {
  if (!_Purchases) {
    const mod = await import('@revenuecat/purchases-capacitor');
    _Purchases = mod.Purchases;
    _LOG_LEVEL = mod.LOG_LEVEL;
    _PAYWALL_RESULT = mod.PAYWALL_RESULT;
    _PURCHASES_ERROR_CODE = mod.PURCHASES_ERROR_CODE;
  }
}

async function loadRevenueCatUI(): Promise<void> {
  if (!_RevenueCatUI) {
    const mod = await import('@revenuecat/purchases-capacitor-ui');
    _RevenueCatUI = mod.RevenueCatUI;
  }
}

// Tracks whether Purchases.configure() has actually been called and completed.
// Any native call before this is true will fatalError inside the RevenueCat
// iOS SDK ("Purchases has not been configured").
let _configurePromise: Promise<void> | null = null;
let _isConfigured = false;

/**
 * Verify RevenueCat is ready before invoking any native call.
 * Awaits an in-flight `initializeRevenueCat()` if one is mid-flight so a fast
 * tap can't race past `Purchases.configure()`.
 */
async function ensureConfigured(): Promise<boolean> {
  if (!isNativeApp() || !REVENUECAT_API_KEY || !hasValidIosRevenueCatKey) {
    return false;
  }
  if (_configurePromise) {
    try {
      await _configurePromise;
    } catch {
      return false;
    }
  }
  return _isConfigured;
}

export function getPaywallResult() {
  return _PAYWALL_RESULT;
}

export function getPurchasesErrorCode() {
  return _PURCHASES_ERROR_CODE;
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Configure the RevenueCat SDK on app launch.
 * Call once from App.tsx useEffect. Sets DEBUG log level in development.
 */
export async function initializeRevenueCat(supabaseUserId?: string): Promise<void> {
  if (!isNativeApp() || !REVENUECAT_API_KEY || !hasValidIosRevenueCatKey) {
    if (isNativeApp() && REVENUECAT_API_KEY && !hasValidIosRevenueCatKey) {
      console.warn('[RevenueCat] Skipping initialization: iOS key must start with "appl_".');
    }
    return;
  }

  if (_configurePromise) {
    return _configurePromise;
  }

  _configurePromise = (async () => {
    await loadPurchases();

    if (process.env.NODE_ENV === 'development' && _LOG_LEVEL) {
      await _Purchases!.setLogLevel({ level: _LOG_LEVEL.DEBUG });
    }

    await _Purchases!.configure({
      apiKey: REVENUECAT_API_KEY,
      appUserID: supabaseUserId ?? undefined,
    });
    _isConfigured = true;
  })();

  try {
    await _configurePromise;
  } catch (error) {
    console.error('[RevenueCat] Failed to initialize:', error);
    _configurePromise = null;
  }
}

// ---------------------------------------------------------------------------
// User identity
// ---------------------------------------------------------------------------

/**
 * Link the RevenueCat customer to the Supabase user ID.
 * Call on SIGNED_IN auth state change.
 */
export async function identifyUser(supabaseUserId: string): Promise<void> {
  if (!isNativeApp() || !REVENUECAT_API_KEY || !hasValidIosRevenueCatKey) return;

  if (!_isConfigured) {
    await initializeRevenueCat(supabaseUserId);
  }

  if (!(await ensureConfigured())) return;

  try {
    await _Purchases!.logIn({ appUserID: supabaseUserId });
  } catch (error) {
    console.error('[RevenueCat] Failed to identify user:', error);
    throw error;
  }
}

/**
 * Reset RevenueCat to an anonymous user on sign-out.
 */
export async function logoutRevenueCat(): Promise<void> {
  if (!(await ensureConfigured())) return;

  try {
    await _Purchases!.logOut();
  } catch (error) {
    console.error('[RevenueCat] Failed to log out:', error);
  }
}

// ---------------------------------------------------------------------------
// Offerings & purchases
// ---------------------------------------------------------------------------

/**
 * Fetch available offerings (products configured in RevenueCat dashboard).
 */
export async function getOfferings() {
  if (!(await ensureConfigured())) return null;

  try {
    const result = await _Purchases!.getOfferings();
    return result;
  } catch (error) {
    console.error('[RevenueCat] Failed to get offerings:', error);
    return null;
  }
}

/**
 * Purchase a specific package (triggers Apple payment sheet).
 * Returns updated customerInfo on success.
 */
export async function purchasePackage(aPackage: any) {
  if (!(await ensureConfigured())) return null;

  const result = await _Purchases!.purchasePackage({ aPackage });
  return result;
}

/**
 * Restore previously purchased subscriptions.
 * Required by App Store Review — must be triggered by explicit user action.
 */
export async function restorePurchases() {
  if (!(await ensureConfigured())) return null;

  const { customerInfo } = await _Purchases!.restorePurchases();
  return customerInfo;
}

// ---------------------------------------------------------------------------
// Customer info & entitlements
// ---------------------------------------------------------------------------

/**
 * Get current customer info (includes entitlements, active subscriptions, etc.).
 */
export async function getCustomerInfo() {
  if (!(await ensureConfigured())) return null;

  try {
    const { customerInfo } = await _Purchases!.getCustomerInfo();
    return customerInfo;
  } catch (error) {
    console.error('[RevenueCat] Failed to get customer info:', error);
    return null;
  }
}

/**
 * Check if the user has the "Wallstreetle Member" entitlement active.
 */
export async function checkEntitlement(): Promise<boolean> {
  const customerInfo = await getCustomerInfo();
  if (!customerInfo) return false;
  return ENTITLEMENT_ID in (customerInfo.entitlements?.active ?? {});
}

/**
 * Listen for real-time customer info updates (subscription changes, renewals, etc.).
 * Returns a cleanup function.
 */
export async function addCustomerInfoListener(
  callback: (info: any) => void
): Promise<() => void> {
  if (!(await ensureConfigured())) return () => {};

  try {
    const listenerToRemove = await _Purchases!.addCustomerInfoUpdateListener(callback);
    return () => {
      _Purchases!.removeCustomerInfoUpdateListener({ listenerToRemove }).catch(() => {});
    };
  } catch (error) {
    console.error('[RevenueCat] Failed to add listener:', error);
    return () => {};
  }
}

// ---------------------------------------------------------------------------
// Paywalls (RevenueCat UI)
// ---------------------------------------------------------------------------

/**
 * Result of a paywall presentation attempt.
 * `shown` distinguishes "paywall actually presented" from "RevenueCat couldn't
 * present at all" (unconfigured, no offering, error) — callers can fall back
 * to a web flow when `shown` is false. `reason` / `diagnostic` are populated
 * on failure to power on-device debug alerts.
 */
export type PaywallFailureReason =
  | 'not-configured'
  | 'no-offering'
  | 'native-not-presented'
  | 'native-error'
  | 'web';

export interface PaywallOutcome {
  shown: boolean;
  purchased: boolean;
  reason?: PaywallFailureReason;
  diagnostic?: string;
}

function notConfiguredOutcome(): PaywallOutcome {
  const diag = {
    isNative: isNativeApp(),
    hasKey: Boolean(REVENUECAT_API_KEY),
    keyPrefix: REVENUECAT_API_KEY.slice(0, 5),
    hasValidIosKey: hasValidIosRevenueCatKey,
    isConfigured: _isConfigured,
  };
  return {
    shown: false,
    purchased: false,
    reason: !isNativeApp() ? 'web' : 'not-configured',
    diagnostic: JSON.stringify(diag, null, 2),
  };
}

/**
 * Preflight the offerings before invoking the native paywall. The iOS
 * RevenueCat SDK fatalErrors / returns NOT_PRESENTED if there is no current
 * offering with packages. Returns a populated PaywallOutcome on failure, or
 * null if there's a presentable offering and we should proceed.
 */
async function offeringPreflight(): Promise<PaywallOutcome | null> {
  try {
    const offerings = await _Purchases!.getOfferings();
    const current = offerings?.current ?? null;
    const packages = current?.availablePackages ?? [];
    if (current && packages.length > 0) {
      return null;
    }
    const diag = {
      hasCurrent: Boolean(current),
      currentIdentifier: current?.identifier ?? null,
      packageCount: packages.length,
      allOfferingIds: Object.keys(offerings?.all ?? {}),
    };
    return {
      shown: false,
      purchased: false,
      reason: 'no-offering',
      diagnostic: JSON.stringify(diag, null, 2),
    };
  } catch (error: any) {
    return {
      shown: false,
      purchased: false,
      reason: 'no-offering',
      diagnostic: JSON.stringify(
        { errorMessage: error?.message ?? String(error) },
        null,
        2,
      ),
    };
  }
}

/**
 * Present the RevenueCat paywall (full-screen modal).
 */
export async function presentPaywall(): Promise<PaywallOutcome> {
  if (!(await ensureConfigured())) return notConfiguredOutcome();

  const preflight = await offeringPreflight();
  if (preflight) {
    console.warn('[RevenueCat] No presentable offering — skipping native paywall.');
    return preflight;
  }

  try {
    await loadRevenueCatUI();
    const PAYWALL_RESULT = _PAYWALL_RESULT!;

    const { result } = await _RevenueCatUI!.presentPaywall({
      presentationConfiguration: { ios: 'FULL_SCREEN' },
    });

    switch (result) {
      case PAYWALL_RESULT.PURCHASED:
      case PAYWALL_RESULT.RESTORED:
        return { shown: true, purchased: true };
      case PAYWALL_RESULT.CANCELLED:
        return { shown: true, purchased: false };
      case PAYWALL_RESULT.NOT_PRESENTED:
        return {
          shown: false,
          purchased: false,
          reason: 'native-not-presented',
          diagnostic: JSON.stringify({ result }, null, 2),
        };
      case PAYWALL_RESULT.ERROR:
      default:
        return {
          shown: false,
          purchased: false,
          reason: 'native-error',
          diagnostic: JSON.stringify({ result }, null, 2),
        };
    }
  } catch (error: any) {
    console.error('[RevenueCat] Failed to present paywall:', error);
    return {
      shown: false,
      purchased: false,
      reason: 'native-error',
      diagnostic: JSON.stringify(
        { errorMessage: error?.message ?? String(error) },
        null,
        2,
      ),
    };
  }
}

/**
 * Present the paywall only if the user does NOT already have the entitlement.
 */
export async function presentPaywallIfNeeded(): Promise<PaywallOutcome> {
  if (!(await ensureConfigured())) return notConfiguredOutcome();

  const preflight = await offeringPreflight();
  if (preflight) {
    console.warn('[RevenueCat] No presentable offering — skipping native paywall.');
    return preflight;
  }

  try {
    await loadRevenueCatUI();
    const PAYWALL_RESULT = _PAYWALL_RESULT!;

    const { result } = await _RevenueCatUI!.presentPaywallIfNeeded({
      requiredEntitlementIdentifier: ENTITLEMENT_ID,
      presentationConfiguration: { ios: 'FULL_SCREEN' },
    });

    switch (result) {
      case PAYWALL_RESULT.PURCHASED:
      case PAYWALL_RESULT.RESTORED:
        return { shown: true, purchased: true };
      case PAYWALL_RESULT.CANCELLED:
        return { shown: true, purchased: false };
      case PAYWALL_RESULT.NOT_PRESENTED:
        return {
          shown: false,
          purchased: false,
          reason: 'native-not-presented',
          diagnostic: JSON.stringify({ result, entitlementId: ENTITLEMENT_ID }, null, 2),
        };
      case PAYWALL_RESULT.ERROR:
      default:
        return {
          shown: false,
          purchased: false,
          reason: 'native-error',
          diagnostic: JSON.stringify({ result }, null, 2),
        };
    }
  } catch (error: any) {
    console.error('[RevenueCat] Failed to present paywall:', error);
    return {
      shown: false,
      purchased: false,
      reason: 'native-error',
      diagnostic: JSON.stringify(
        { errorMessage: error?.message ?? String(error) },
        null,
        2,
      ),
    };
  }
}

// ---------------------------------------------------------------------------
// Customer Center (RevenueCat UI)
// ---------------------------------------------------------------------------

/**
 * Present the RevenueCat Customer Center for subscription management.
 * Handles cancellation, plan changes, restore, and refund requests.
 * Only supported on iOS and Android (not web).
 */
export async function presentCustomerCenter(): Promise<void> {
  if (!(await ensureConfigured())) return;

  try {
    await loadRevenueCatUI();
    await _RevenueCatUI!.presentCustomerCenter();
  } catch (error) {
    console.error('[RevenueCat] Failed to present customer center:', error);
  }
}
